import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroller";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Tabs from "src/app/components/Tabs/Tabs";
import Tab from "src/app/components/Tabs/Tab";
import NoResultsScreen from "src/app/components/NoResultsScreen/NoResultsScreen";
import { Button } from "src/app/components/Button/Button";
import errorImg from "src/images/something_went_wrong.svg";
import EmptyResultIcon from "src/images/empty-audience-result.png";
import { ReactComponent as PlusIcon } from "src/images/plus-transparent.svg";
import AddSingleSocialProfileModal from "src/app/modals/AddSingleSocialProfileModal/AddSingleSocialProfileModal";
import {
  CDTable,
  CDTableBody,
  CDTableHead,
} from "../../components/CDTable/CDTable";
import Loader from "../../components/Loader/Loader";
import PendingUsersHandler from "./PendingUsersHandler/PendingUsersHandler";
import {
  selectInfluencer,
  selectMultipleInfluencer,
} from "../CreatorSearch/redux/creatorSearchActions";
import { getCreatorResultsColumns } from "./utils/getCreatorResultsColumns";
import CreatorResultRow from "./CreatorResultRow";

export default function CreatorResultsTable({
  userList,
  endOfList,
  labelsHeight,
  searchData,
  getListData,
  getMoreResults,
  listLoading,
  getScrollParent,
  toggleFilters,
  tableSearch,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
}) {
  const dispatch = useDispatch();

  const [selectedAll, setSelectedAll] = useState(false);
  const handleSortColumn = (columnKey, newOrder) => {
    if (columnKey !== sortBy) {
      setSortBy?.(columnKey);
    }
    setSortOrder?.(newOrder || "desc");
  };
  const [selectedAllSimilar, setSelectedAllSimilar] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [showAddSocialProfileModal, setShowAddSocialProfileModal] =
    useState(false);

  const similarProfiles = useSelector(
    (state) => state.creatorSearchReducer.similarProfiles,
  );
  const searchAudienceFilters = useSelector(
    (state) => state.creatorSearchReducer.searchAudienceFilters,
  );
  const selectedInfluencersOnResults = useSelector(
    (state) => state.creatorSearchReducer.selectedInfluencersOnResults,
  );
  const pendingUsers = useSelector(
    (state) => state.creatorSearchReducer.pendingUsers,
  );
  const isAudienceDataError = useSelector(
    (state) => state.creatorSearchReducer.isAudienceDataError,
  );
  const isResultsLimitReached = useSelector(
    (state) => state.creatorSearchReducer.isResultsLimitReached,
  );
  const columns = useSelector((state) => state.creatorSearchReducer.columns);
  const hiddenColumns = useSelector(
    (state) => state.creatorSearchReducer.hiddenColumns,
  );
  const visibleColumns = columns.filter(
    (column) => !hiddenColumns.includes(column.key),
  );

  const selectedUsersOnPageCount = useCallback(() => {
    return userList.filter((user) => {
      return selectedInfluencersOnResults?.indexOf(user.userId) > -1;
    }).length;
  }, [userList, selectedInfluencersOnResults]);

  const selectedSimilarProfilesOnPageCount = useCallback(() => {
    return similarProfiles?.filter((user) => {
      return selectedInfluencersOnResults?.indexOf(user.userId) > -1;
    }).length;
  }, [similarProfiles, selectedInfluencersOnResults]);

  useEffect(() => {
    setSelectedAll(false);
    setSelectedAllSimilar(false);
  }, [userList]);

  useEffect(() => {
    if (selectedUsersOnPageCount() === userList.length) {
      setSelectedAll(true);
    }
    if (
      similarProfiles.length &&
      selectedSimilarProfilesOnPageCount() === similarProfiles.length
    ) {
      setSelectedAllSimilar(true);
    }
    if (!selectedUsersOnPageCount()) {
      setSelectedAll(false);
    }
  }, [
    selectedInfluencersOnResults,
    userList,
    similarProfiles,
    selectedSimilarProfilesOnPageCount,
    selectedUsersOnPageCount,
  ]);

  const onSelectAllClick = async (list, value, setValue) => {
    const userIds = [];
    const profileUrls = [];

    list.forEach((influencer) => {
      userIds.push(influencer.userId);
      profileUrls.push(influencer.profileUrl);
    });

    dispatch(selectMultipleInfluencer(userIds, profileUrls, !value));
    setValue(!value);
  };

  const selectUserClick = (userId, profileUrl) => {
    if (selectedUsersOnPageCount() === userList.length) {
      setSelectedAll(false);
    }

    dispatch(selectInfluencer(userId, profileUrl));
  };

  const handleRangeSelect = (start, end, list, value) => {
    const minIndex = Math.min(start, end);
    const maxIndex = Math.max(start, end);

    const selectionIndexes = [];
    for (let i = minIndex; i <= maxIndex; i++) {
      selectionIndexes.push(i);
    }

    const userIds = [];
    const profileUrls = [];
    selectionIndexes.forEach((index) => {
      const influencer = list[index];
      if (influencer) {
        userIds.push(influencer.userId);
        profileUrls.push(influencer.profileUrl);
      }
    });

    dispatch(selectMultipleInfluencer(userIds, profileUrls, value));
  };

  const renderList = (list) => {
    return list.map((influencer, index) => (
      <CreatorResultRow
        key={influencer.userId}
        influencer={influencer}
        index={index}
        columns={visibleColumns}
        selectUserClick={selectUserClick}
        onRangeSelect={(start, end, value) =>
          handleRangeSelect(start, end, list, value)
        }
        lastSelectedIndex={lastSelectedIndex}
        setLastSelectedIndex={setLastSelectedIndex}
        highlightedCellKeys={tableSearch.highlightedCellKeys}
        activeResult={tableSearch.activeResult}
        searchData={searchData}
      />
    ));
  };

  const renderAddSocialProfileHint = () => {
    if (searchData.influencerSocialUsername) {
      return (
        <div className="audience-results-page__add-social-profile">
          <div className="audience-results-page__add-social-profile-content">
            <IDHFormattedMessage
              id="front_did_you_not_find_the_profile_you_were_looking_for"
              defaultMessage="Did you not find the profile you were looking for?"
            />
            <Button
              variant="white-to-light"
              size="small"
              onClick={() => setShowAddSocialProfileModal(true)}
            >
              <PlusIcon />
              <IDHFormattedMessage
                id="front_add_social_profile"
                defaultMessage="Add social profile"
              />
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderSimilarProfiles = () => {
    if (similarProfiles.length) {
      return (
        <>
          <tbody className="cdt-table__full-row-group">
            <tr>
              <td colSpan={999} className="cdt-table__full-row-cell">
                <div className="audience-results-page__similar-profile-tabs">
                  <div className="audience-results-page__tabs">
                    <Tabs>
                      <Tab
                        tabText={
                          <IDHFormattedMessage
                            id="front_similar_profiles"
                            defaultMessage="Similar profiles"
                          />
                        }
                        count={similarProfiles.length}
                        active
                        big
                        noClickable
                      />
                    </Tabs>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <CDTableHead
            columns={getCreatorResultsColumns({
              list: similarProfiles,
              checked: selectedAllSimilar,
              onSelectAllClick: () =>
                onSelectAllClick(
                  similarProfiles,
                  selectedAllSimilar,
                  setSelectedAllSimilar,
                ),
              endOfList,
              columns: visibleColumns,
              sortBy,
              sortOrder,
              onSortColumn: handleSortColumn,
              showSortArrows: false,
            })}
          />
          <CDTableBody>{renderList(similarProfiles)}</CDTableBody>
        </>
      );
    }

    return null;
  };

  const renderTable = () => {
    return (
      <CDTable
        columns={getCreatorResultsColumns({
          list: userList,
          checked: selectedAll,
          onSelectAllClick: () =>
            onSelectAllClick(userList, selectedAll, setSelectedAll),
          endOfList,
          columns: visibleColumns,
          sortBy,
          sortOrder,
          onSortColumn: handleSortColumn,
        })}
        autoLayout
        showNoResults={userList.length === 0}
        customNoResults={
          <>
            {isAudienceDataError ? (
              <div className="no-results-screen-wrapper">
                <NoResultsScreen
                  title={
                    <IDHFormattedMessage
                      id="front_audience_results_data_error_title"
                      defaultMessage="Oops. Something went wrong..."
                    />
                  }
                  subtitle={
                    <IDHFormattedMessage
                      id="front_audience_results_data_error_description"
                      defaultMessage="An unexpected error occurred. Please try again."
                    />
                  }
                  bottomContent={
                    <Button size="large" variant="blue" onClick={toggleFilters}>
                      <IDHFormattedMessage
                        id="front_open_filters"
                        defaultMessage="Open filters"
                      />
                    </Button>
                  }
                  imgUrl={errorImg}
                />
              </div>
            ) : (
              <PendingUsersHandler
                influencerSocialUsername={
                  searchAudienceFilters.influencerSocialUsername
                }
                getListData={getListData}
                pendingUsers={pendingUsers}
              />
            )}
            {renderAddSocialProfileHint()}
          </>
        }
        headStyle={{ top: 116 + labelsHeight }}
      >
        <CDTableBody>{renderList(userList)}</CDTableBody>

        {searchData.influencerSocialUsername && (
          <tbody className="cdt-table__full-row-group">
            <tr>
              <td colSpan={999} className="cdt-table__full-row-cell">
                {renderAddSocialProfileHint()}
              </td>
            </tr>
          </tbody>
        )}

        {renderSimilarProfiles()}
      </CDTable>
    );
  };

  const renderResultsLimitInfo = () => {
    if (!isResultsLimitReached || userList.length === 0) {
      return null;
    }

    return (
      <div className="audience-results__limit-info">
        <NoResultsScreen
          title={
            <IDHFormattedMessage
              id="front_creator_search_results_limit_title"
              defaultMessage="Looking for more creators?"
            />
          }
          subtitle={
            <IDHFormattedMessage
              id="front_creator_search_results_limit_subtitle"
              defaultMessage="Refine your filters to narrow your search and discover additional matches."
            />
          }
          bottomContent={
            <Button size="large" variant="blue" onClick={toggleFilters}>
              <IDHFormattedMessage
                id="front_open_filters"
                defaultMessage="Open filters"
              />
            </Button>
          }
          imgUrl={EmptyResultIcon}
        />
      </div>
    );
  };

  return (
    <>
      <InfiniteScroll
        pageStart={0}
        initialLoad={false}
        loadMore={() => getMoreResults(endOfList)}
        className="audience-results__scroller"
        hasMore={!endOfList}
        loader={listLoading && <Loader />}
        useWindow={false}
        getScrollParent={getScrollParent}
      >
        {renderTable()}
      </InfiniteScroll>
      {renderResultsLimitInfo()}

      {showAddSocialProfileModal && (
        <AddSingleSocialProfileModal
          onClose={() => setShowAddSocialProfileModal(false)}
        />
      )}
    </>
  );
}
